/**
 * CoPilotPanel — read-only side drawer (Phase 5 of the Feb 2026 redesign).
 *
 * What it does
 * ────────────
 *   • Slides in from the right when the user clicks the floating Co-pilot
 *     button (mounted in App.js).
 *   • Top half shows a **contextual explainer** for the current wizard step.
 *   • Bottom half is a **Q&A chat** — the user can ask anything; the answer
 *     comes from the LoopAI proxy via `requestCoPilotChat`.
 *
 * Read-only guarantee
 * ───────────────────
 *   The panel never mutates project state. It calls `requestCoPilotChat`
 *   with a sanitized read-only snapshot (`projectSummary`) and renders the
 *   LLM's text response. Any "make this happen" actions are deferred to
 *   Phase 6.
 *
 * Props
 * ─────
 *   isOpen          — boolean drawer state.
 *   onClose         — close-handler (also called by the Escape key).
 *   currentAppStep  — current wizard step (used for the explainer + LLM context).
 *   project         — full activeProject (we sanitize before sending).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Send, Loader2, Zap, AlertTriangle } from 'lucide-react';
import { getExplainer } from './coPilotExplainers';
import { validateActions } from './coPilotActions';
import { requestCoPilotChat } from '../../services/aiService';

const buildProjectSummary = (project = {}) => {
    if (!project) return {};
    const scriptPreview = (project.script || '').slice(0, 400);
    return {
        name:           project.name || '',
        duration:       project.duration || '',
        aspectRatio:    project.aspectRatio || '',
        tier:           project.tier || '',
        videoStyle:     project.videoStyle || '',
        audioStyle:     project.audioStyle || '',
        industryId:     project.industryId || '',
        videoTypeId:    project.videoTypeId || '',
        brandName:      project.brandName || '',
        characterCount: Array.isArray(project.characters) ? project.characters.length : 0,
        elementCount:   Array.isArray(project.characters) ? project.characters.filter(c => c.category === 'element').length : 0,
        scriptPreview,
        sceneCount:     Array.isArray(project.scenes) ? project.scenes.length : 0,
    };
};

const Bubble = ({ role, text, isLoading }) => {
    const isUser = role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    isUser
                        ? 'bg-violet-600 text-white rounded-br-sm'
                        : 'bg-white/[0.05] border border-white/10 text-zinc-200 rounded-bl-sm'
                }`}
                data-testid={`copilot-bubble-${role}`}
            >
                {isLoading ? (
                    <span className="inline-flex items-center gap-1.5 text-zinc-400">
                        <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                    </span>
                ) : text}
            </div>
        </div>
    );
};

export const CoPilotPanel = ({ isOpen, onClose, currentAppStep, project, onApplyAction }) => {
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState(null);
    // Phase 6: destructive-action confirm modal. Pending action is held
    // here until the user clicks "Apply" or "Cancel" in the modal.
    const [pendingAction, setPendingAction] = useState(null);
    const listRef = useRef(null);

    const explainer = getExplainer(currentAppStep);

    // Auto-scroll on new messages.
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, isSending]);

    // Close on Escape.
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    const handleSend = async () => {
        const q = input.trim();
        if (!q || isSending) return;
        setError(null);
        setInput('');
        const next = [...messages, { role: 'user', content: q }];
        setMessages(next);
        setIsSending(true);
        try {
            const result = await requestCoPilotChat({
                question: q,
                step: currentAppStep,
                history: next.map(m => ({ role: m.role, content: m.content })),
                project: buildProjectSummary(project),
                // Brand scope (Feb 2026) — forwarded so the co-pilot sees the
                // same scoped brand/products the user picked on Story.
                brandScope: {
                    brandId:    project?.brandId    || null,
                    productIds: project?.productIds || [],
                    serviceIds: project?.serviceIds || [],
                },
            });
            // Phase 6: filter LLM-proposed actions through the per-step
            // allow-list. Anything not whitelisted is silently dropped.
            const rawActions = Array.isArray(result.actions) ? result.actions : [];
            const safeActions = validateActions(rawActions, currentAppStep);
            // Diagnostic logging — helps us see whether the admin backend
            // is returning `actions[]` AND whether anything is being dropped
            // by the allow-list. Strip these once Phase 6 is verified live.
            if (rawActions.length > 0 || safeActions.length > 0) {
                console.info('[Co-pilot] raw actions from admin:', rawActions);
                console.info('[Co-pilot] after allow-list filter for step', currentAppStep, ':', safeActions);
                if (rawActions.length > safeActions.length) {
                    const droppedTypes = rawActions
                        .filter(a => !safeActions.find(s => s.type === a?.type))
                        .map(a => a?.type || '(no type)');
                    console.warn('[Co-pilot] dropped actions (not in allow-list or bad payload):', droppedTypes);
                }
            } else {
                console.info('[Co-pilot] admin returned NO actions for step:', currentAppStep);
            }
            setMessages(curr => [...curr, {
                role: 'assistant',
                content: result.answer || '…',
                actions: safeActions,
                appliedActionIds: [],
            }]);
        } catch (e) {
            setError(e?.message || 'Co-pilot is offline right now.');
        } finally {
            setIsSending(false);
        }
    };

    // Phase 6: apply (or queue for confirmation) a Co-pilot-proposed action.
    const handleActionClick = (msgIdx, action) => {
        if (!action || !onApplyAction) return;
        if (action.destructive) {
            setPendingAction({ msgIdx, action });
            return;
        }
        applyAction(msgIdx, action);
    };

    const applyAction = (msgIdx, action) => {
        try {
            onApplyAction(action);
            setMessages(curr => curr.map((m, i) => (
                i === msgIdx
                    ? { ...m, appliedActionIds: [...(m.appliedActionIds || []), action.id] }
                    : m
            )));
        } catch (e) {
            setError(e?.message || 'Could not apply that action.');
        }
    };

    const confirmPendingAction = () => {
        if (!pendingAction) return;
        applyAction(pendingAction.msgIdx, pendingAction.action);
        setPendingAction(null);
    };

    return (
        <>
            {/* Phase 6: Destructive-action confirm modal */}
            {pendingAction && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    data-testid="copilot-confirm-modal"
                    onClick={() => setPendingAction(null)}
                >
                    <div
                        className="w-full max-w-md rounded-2xl border border-amber-500/40 bg-[#0a0a0f] p-5 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
                                <AlertTriangle className="w-5 h-5 text-amber-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base font-bold text-white">Confirm action</h3>
                                <p className="text-sm text-zinc-300 mt-1">{pendingAction.action.label}</p>
                                <p className="text-[11px] text-amber-300 mt-2">
                                    This change overwrites existing content. You can&apos;t auto-undo it from the Co-pilot.
                                </p>
                                {/* Action-specific preview */}
                                {pendingAction.action.type === 'script.replace' && (
                                    <div className="mt-3 rounded-lg bg-white/[0.04] border border-white/10 p-2 max-h-32 overflow-y-auto" data-testid="copilot-confirm-preview">
                                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">New script preview</div>
                                        <pre className="text-[11px] text-zinc-200 whitespace-pre-wrap font-sans">
                                            {String(pendingAction.action.payload?.value || '').slice(0, 600)}
                                            {String(pendingAction.action.payload?.value || '').length > 600 && '…'}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2 mt-5">
                            <button
                                type="button"
                                onClick={() => setPendingAction(null)}
                                className="flex-1 px-3 py-2 rounded-xl border border-white/15 text-sm text-zinc-300 hover:bg-white/5 transition-colors"
                                data-testid="copilot-confirm-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmPendingAction}
                                className="flex-1 px-3 py-2 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold transition-colors"
                                data-testid="copilot-confirm-apply"
                            >
                                Apply anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Phase 6.1 (Feb 2026): Co-pilot is now a PERSISTENT right rail
                inside the wizard's flex layout (no fixed positioning, no
                backdrop). When `isOpen=true` it's a full 384px column;
                when `isOpen=false` it collapses to a 48px strip with the
                expand button — so it never disappears from the user's view. */}
            {!isOpen ? (
                /* Collapsed strip */
                <aside
                    className="h-full w-12 flex-shrink-0 border-l border-white/10 bg-[#0a0a0f]/80 backdrop-blur-sm flex flex-col items-center py-3 gap-3"
                    data-testid="copilot-rail-collapsed"
                >
                    <button
                        type="button"
                        onClick={onClose /* prop is the toggle */}
                        className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center hover:bg-violet-500/30 transition-colors"
                        title="Open Co-pilot"
                        data-testid="copilot-rail-expand-btn"
                        aria-label="Expand Co-pilot"
                    >
                        <Sparkles className="w-4 h-4 text-violet-300" />
                    </button>
                    <div
                        className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500"
                        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                    >
                        Co-pilot
                    </div>
                </aside>
            ) : (
            <aside
                className="h-full w-full sm:w-[384px] flex-shrink-0 border-l border-white/10 bg-[#0a0a0f] flex flex-col"
                role="complementary"
                aria-label="Co-pilot"
                data-testid="copilot-panel"
            >
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
                    <div className="w-9 h-9 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-violet-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white">Co-pilot</div>
                        <div className="text-[11px] text-zinc-500 truncate">Tips for this step · ask anything</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                        data-testid="copilot-close-btn"
                        aria-label="Close co-pilot"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body — scrollable */}
                <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    {/* Contextual explainer */}
                    {explainer && (
                        <div className="rounded-2xl border border-violet-500/20 bg-violet-500/[0.04] p-3" data-testid="copilot-explainer">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-300 mb-1">{explainer.title}</div>
                            <p className="text-sm text-zinc-200 leading-relaxed" dangerouslySetInnerHTML={{ __html: explainer.summary }} />
                            {explainer.tips && explainer.tips.length > 0 && (
                                <ul className="mt-2.5 space-y-1.5">
                                    {explainer.tips.map((t, i) => (
                                        <li key={i} className="text-[12px] text-zinc-300 leading-snug flex gap-2">
                                            <span className="text-violet-400 mt-0.5">•</span>
                                            <span dangerouslySetInnerHTML={{ __html: t }} />
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {explainer.examples && explainer.examples.length > 0 && (
                                <div className="mt-3 pt-2.5 border-t border-violet-500/20">
                                    <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-300/80 mb-1">Try saying</div>
                                    {explainer.examples.map((ex, i) => (
                                        <div key={i} className="text-[11px] text-zinc-400 italic leading-snug">{ex}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Chat messages */}
                    {messages.length > 0 && (
                        <div className="space-y-2.5" data-testid="copilot-messages">
                            {messages.map((m, i) => (
                                <React.Fragment key={i}>
                                    <Bubble role={m.role} text={m.content} />
                                    {/* Phase 6: action chips below assistant
                                        answers. Already-applied chips show
                                        an "Applied" check and disable. */}
                                    {m.role === 'assistant' && Array.isArray(m.actions) && m.actions.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 ml-1 mt-1" data-testid={`copilot-actions-${i}`}>
                                            {m.actions.map(a => {
                                                const applied = (m.appliedActionIds || []).includes(a.id);
                                                return (
                                                    <button
                                                        key={a.id}
                                                        type="button"
                                                        disabled={applied}
                                                        onClick={() => handleActionClick(i, a)}
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
                                                            applied
                                                                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 cursor-default'
                                                                : a.destructive
                                                                    ? 'bg-amber-500/10 border border-amber-500/40 text-amber-200 hover:bg-amber-500/20'
                                                                    : 'bg-violet-500/15 border border-violet-500/40 text-violet-100 hover:bg-violet-500/25 hover:-translate-y-0.5'
                                                        }`}
                                                        data-testid={`copilot-action-${a.id}`}
                                                        title={a.destructive ? 'Needs confirmation before applying' : 'Apply'}
                                                    >
                                                        {applied
                                                            ? <><Zap className="w-3 h-3" /> Applied</>
                                                            : a.destructive
                                                                ? <><AlertTriangle className="w-3 h-3" /> {a.label}</>
                                                                : <><Zap className="w-3 h-3" /> {a.label}</>
                                                        }
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                            {isSending && <Bubble role="assistant" isLoading />}
                        </div>
                    )}

                    {/* Empty-state suggestion */}
                    {messages.length === 0 && (
                        <div className="text-[11px] text-zinc-500 px-1">
                            Ask anything — for example: &ldquo;What duration works best for Reels?&rdquo; or &ldquo;Is Standard tier enough for a paid IG ad?&rdquo;
                        </div>
                    )}

                    {/* Error toast */}
                    {error && (
                        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-200" data-testid="copilot-error">
                            {error}
                        </div>
                    )}
                </div>

                {/* Input */}
                <div className="border-t border-white/10 p-3 bg-[#0a0a0f]">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            placeholder="Ask the Co-pilot…"
                            rows={1}
                            disabled={isSending}
                            className="flex-1 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none min-h-[40px] max-h-32 disabled:opacity-50"
                            data-testid="copilot-input"
                        />
                        <button
                            type="button"
                            onClick={handleSend}
                            disabled={isSending || !input.trim()}
                            className="h-10 px-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:bg-zinc-800 disabled:text-zinc-600 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                            data-testid="copilot-send-btn"
                            aria-label="Send question"
                        >
                            {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </button>
                    </div>
                    <div className="text-[10px] text-zinc-600 mt-1.5 px-1">Read-only · Co-pilot can&apos;t change your project · 1 credit/answer</div>
                </div>
            </aside>
            )}
        </>
    );
};

/**
 * Legacy launcher — kept for backwards compatibility but no longer mounted
 * by App.js. The Co-pilot is now a persistent right rail; the rail's own
 * collapse button replaces the floating launcher.
 */
export const CoPilotLauncher = ({ isOpen, onToggle }) => (
    <button
        type="button"
        onClick={onToggle}
        className={`fixed bottom-6 right-6 z-30 flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl shadow-violet-600/40 ring-1 ring-violet-300/40 transition-all hover:-translate-y-0.5 ${
            isOpen ? 'bg-violet-700' : 'bg-violet-600 hover:bg-violet-500'
        }`}
        data-testid="copilot-launcher"
        aria-label={isOpen ? 'Close co-pilot' : 'Open co-pilot'}
    >
        <Sparkles className="w-4 h-4 text-white" />
        <span className="text-sm font-semibold text-white hidden sm:inline">Co-pilot</span>
    </button>
);

export default CoPilotPanel;
